import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as Yup from "yup"
import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { vehicleType } from "../../../lib/GlobalVariables"
import { _numberRegex_ } from "../../../lib/Regex"
import moment from "moment"

const AddEditVehicle = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  setData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [brandData, setBrandData] = useState(false)
  const [connectorData, setConnectorData] = useState(false)

  const navigate = useNavigate()

  const validationSchema = Yup.object({
    model: Yup.string()
      .min(2, "Enter at least 2 character for Vehicle Name")
      .required("Model name is required"),
    vehicle_img: Yup.mixed()
      .test("required", "Vehicle image is required", (file) => {
        if (isUpdate) {
          return file !== null && file !== undefined
        }
        return file
      })
      .test(
        "fileType",
        "File format is not supported, Please use .jpg/.png/.jpeg",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return (
            file && ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
          )
        }
      )
      .test(
        "fileSize",
        "File size too large, max file size is 500 Kb",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return file && file.size <= 512000
        }
      ),
    brand: Yup.object().required("Brand is required"),
    manufacturing_year: Yup.object().required("Manufacturing Year is required"),
    vehicle_type: Yup.object()?.required("Vehicle type is required"),
    battery_capacity: Yup.string().required(
      "Vehicle battery capacity is required"
    ),
    battery_voltage: Yup.string().required(
      "Vehicle battery voltage is required "
    ),
    supported_connectors: Yup.array()
      .min(1, "At least one connector is required")
      .max(2, "Only 2 connector can be assigned to single vehicle")
      .required("Connector is required "),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      values = {
        ...values,
        brand: values?.brand?.value,
        vehicle_type: values?.vehicle_type?.label,
        manufacturing_year: values?.manufacturing_year?.label,
        vehicle_img:
          typeof values?.vehicle_img === "object"
            ? values?.vehicle_img
            : isUpdate.vehicle_img,
        supported_connectors: Array.isArray(values.supported_connectors)
          ? values.supported_connectors.map((connector) =>
              Number(connector.value)
            )
          : values.supported_connectors,
      }

      const formData = new FormData()
      Object.keys(values).forEach((key) => {
        if (Array.isArray(values[key])) {
          formData.append(key, JSON.stringify(values[key]))
        } else {
          formData.append(key, values[key])
        }
      })

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateVehicle + isUpdate.id
          : admin.createVehicle,
        data: formData,
      })

      if (response?.data?.code === 200 || response?.data?.code === 201) {
        toast.success(response.data.message)
        setShow(false)
        setLoading(false)
        isUpdate
          ? setData((prevData) =>
              prevData.map((item) =>
                item.id === isUpdate.id
                  ? {
                      ...item,
                      ...response.data.data,
                    }
                  : item
              )
            )
          : fetchData("")
        handleClose()
        resetForm()
      } else {
        setLoading(false)
        throw { message: response?.data?.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          model: isUpdate.model,
          vehicle_img: isUpdate?.vehicle_img?.split("/").pop(),
          brand: {
            value: isUpdate?.brand.id,
            label: isUpdate?.brand.brand_name,
          },
          vehicle_type: {
            value: isUpdate?.vehicle_type,
            label: isUpdate?.vehicle_type,
          },
          manufacturing_year: {
            value: isUpdate?.manufacturing_year,
            label: isUpdate?.manufacturing_year,
          },
          battery_capacity: isUpdate.battery_capacity,
          battery_voltage: isUpdate.battery_voltage,
          supported_connectors: isUpdate.connectors.map((data) => ({
            value: data.id,
            label: data.connector_type,
          })),
        }
      : {
          model: "",
          vehicle_img: "",
          brand: "",
          vehicle_type: "",
          battery_capacity: "",
          manufacturing_year: "",
          battery_voltage: "",
          supported_connectors: "",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm, initialValues } =
    formik

  const fetchBrandData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.brandList,
      })
      const data = response?.data?.data.map(({ id, brand_name }) => ({
        value: id,
        label: brand_name,
      }))
      setBrandData(data)
    } catch (error) {
      toast.error(error)
    }
  }

  const fetchConnectorData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.connectorList,
      })
      const data = response?.data?.data?.map(({ id, connector_type }) => ({
        value: id,
        label: connector_type,
      }))
      setConnectorData(data)
    } catch (error) {
      toast.error(error)
    }
  }

  const currentYear = moment().year()
  const years = Array.from(
    { length: 11 },
    (_, index) => currentYear - index
  ).map((year) => ({
    value: year,
    label: year.toString(),
  }))

  useEffect(() => {
    fetchBrandData()
    fetchConnectorData()
  }, [])

  return (
    <>
      <ModalComponent
        show={show}
        modalsize={"lg"}
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Vehicle" : "Add Vehicle"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext={"Cancel"}
        handleSubmit={formik.handleSubmit}
        children={
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <FloatingLabel
                  controlId="vehicle_img"
                  label={
                    <>
                      Vehicle image<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  {values?.vehicle_img ? (
                    <div
                      className={`previewnm_ip ${
                        touched?.vehicle_img && errors?.vehicle_img
                          ? "is-invalid"
                          : ""
                      }`}
                    >
                      <TooltipComponent
                        toolplace="top"
                        tooltipText={
                          typeof values?.vehicle_img === "object"
                            ? values?.vehicle_img.name
                            : values?.vehicle_img
                        }
                      >
                        <p className="mb-0 previewnm_title">
                          {typeof values?.vehicle_img === "object"
                            ? values?.vehicle_img.name
                            : values?.vehicle_img}
                        </p>
                      </TooltipComponent>
                      <span
                        className="remove_previewnm"
                        onClick={() => {
                          setFieldValue("vehicle_img", "")
                        }}
                      >
                        <i className="icon_main cross_icon"></i>
                      </span>
                    </div>
                  ) : (
                    <Form.Control
                      type="file"
                      placeholder=""
                      autoComplete="false"
                      name="vehicle_img"
                      onChange={async (e) => {
                        setFieldValue("vehicle_img", e?.target?.files[0])
                      }}
                      isInvalid={touched.vehicle_img && errors.vehicle_img}
                      isValid={touched.vehicle_img && !errors.vehicle_img}
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.vehicle_img}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Brand<span className="required_star">*</span>
                </label>
                <CustomSelect
                  value={values?.brand}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("brand", selectedOption)
                  }}
                  options={brandData}
                  isMulti={false}
                  placeholder={"Select Brand"}
                />
                {touched?.brand && errors.brand && (
                  <p className="invalid_error">{errors.brand}</p>
                )}
              </Col>
              <Col xs={6} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Manufacturing Year<span className="required_star">*</span>
                </label>
                <CustomSelect
                  value={values?.manufacturing_year}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("manufacturing_year", selectedOption)
                  }}
                  options={years}
                  isMulti={false}
                  placeholder={"Select Manufacturing Year"}
                />
                {touched?.manufacturing_year && errors.manufacturing_year && (
                  <p className="invalid_error">{errors.manufacturing_year}</p>
                )}
              </Col>
              <Col xs={12}>
                <FloatingLabel
                  controlId="modelName"
                  label={
                    <>
                      Model Name<span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="model"
                    autoComplete="false"
                    maxLength={30}
                    value={values?.model}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.model && !!errors.model}
                    isValid={touched.model && !errors.model}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.model}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Vehicle Type<span className="required_star">*</span>
                </label>
                <CustomSelect
                  value={values?.vehicle_type}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("vehicle_type", selectedOption)
                  }}
                  options={vehicleType}
                  isMulti={false}
                  placeholder={"Select Vehicle Type"}
                />
                {touched?.vehicle_type && errors.vehicle_type && (
                  <p className="invalid_error">{errors.vehicle_type}</p>
                )}
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="brandname"
                  label={
                    <>
                      Battery Capacity
                      <small style={{ fontSize: "0.75rem" }}>(kW/h)</small>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="battery_capacity"
                    autoComplete="false"
                    maxLength={10}
                    value={values.battery_capacity}
                    onChange={(e) => handleOnchange(e, _numberRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={
                      touched.battery_capacity && errors.battery_capacity
                    }
                    isValid={
                      touched.battery_capacity && !errors.battery_capacity
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.battery_capacity}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="brandname"
                  label={
                    <>
                      Battery Voltage
                      <small style={{ fontSize: "0.75rem" }}>(v)</small>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="battery_voltage"
                    autoComplete="false"
                    maxLength={10}
                    value={values?.battery_voltage}
                    onChange={(e) => handleOnchange(e, _numberRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={
                      touched.battery_voltage && !!errors.battery_voltage
                    }
                    isValid={touched.battery_voltage && !errors.battery_voltage}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.battery_voltage}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Connectors<span className="required_star">*</span>
                </label>
                <CustomSelect
                  value={values?.supported_connectors}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("supported_connectors", selectedOption)
                  }}
                  options={connectorData}
                  isMulti={true}
                  placeholder={"Select type of Connector"}
                />
                {touched?.supported_connectors &&
                  errors.supported_connectors && (
                    <p className="invalid_error">
                      {errors.supported_connectors}
                    </p>
                  )}
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  )
}

export default AddEditVehicle
